import {
  DEFAULT_APPEARANCE_OPTIONS,
  DEFAULT_DRESSING_OPTIONS,
  DEFAULT_PAGE_OPTIONS,
  DEFAULT_TABLE_OPTIONS,
} from '@mss_creator/shared';
import type {
  ExportAppearanceOptions,
  ExportDressingOptions,
  ExportFit,
  ExportPageOptions,
  ExportSubjectDescriptionOptions,
  ExportTableOptions,
} from '@mss_creator/shared';

/**
 * Export customisation defaults and pure state transitions — export-rework
 * chantier 1 (spec R1-R5), superseding the 2026-08-25 doc's single
 * `ExportPdfOptions` blob.
 *
 * The state is the wire contract's own four blocks rather than a local shape
 * mapped onto them at request time: the panel and the request then cannot
 * disagree about what a setting means, which is the class of drift the whole
 * rework exists to remove.
 */
export interface ExportOptionsState {
  page: ExportPageOptions;
  table: ExportTableOptions;
  dressing: ExportDressingOptions;
  appearance: ExportAppearanceOptions;
}

/**
 * The panel's entry state: the wire's own « Lecture » preset (spec P2), so an
 * untouched panel and an omitted request block produce the same document.
 *
 * A factory returning fresh clones, not a shared constant: `table` carries
 * mutable containers (`columnWidths`, `columnOrder`, `pageBreaksBefore`), and
 * aliasing the exported `DEFAULT_*` objects into React state would let one
 * in-place edit corrupt them process-wide — the same hazard the schema's
 * cloning `.default()` factories exist to avoid.
 */
export function defaultExportOptions(): ExportOptionsState {
  return {
    page: structuredClone(DEFAULT_PAGE_OPTIONS),
    table: structuredClone(DEFAULT_TABLE_OPTIONS),
    dressing: structuredClone(DEFAULT_DRESSING_OPTIONS),
    appearance: structuredClone(DEFAULT_APPEARANCE_OPTIONS),
  };
}

/** Decision C6 fixes the default position ("before"); `include` defaults off. */
export const DEFAULT_SUBJECT_DESCRIPTION: ExportSubjectDescriptionOptions = {
  include: false,
  position: 'before',
};

/**
 * Spec R1. Each label names the *outcome* the user wants — the whole point of
 * replacing the old `pagesWide`/`pagesTall` spinners, which described the
 * bin-packing mechanism and whose `auto`/`auto` default silently meant "never
 * shrink". The control is labelled « Ajustement »: it is no longer a scale
 * picker at all, since the free-form percent is retired.
 */
export const FIT_LABELS: Record<ExportFit, string> = {
  aucun: 'Aucun — pagination libre',
  'une-page-de-large': 'Une page de large',
  'une-page-de-haut': 'Une page de haut',
  'une-seule-page': 'Tout sur une seule page',
};

/** Short explanation shown under the picker, so the choice needs no guesswork. */
export const FIT_HINTS: Record<ExportFit, string> = {
  aucun: 'Aucune réduction — la matrice est répartie sur autant de pages que nécessaire.',
  'une-page-de-large': 'Toutes les colonnes sur une seule largeur de page.',
  'une-page-de-haut': 'Toutes les lignes sur une seule hauteur de page.',
  'une-seule-page': 'La matrice entière sur une page, quelle que soit la réduction.',
};

/**
 * Decision L5. Below this, the rendered type is too small to read comfortably
 * on paper — roughly the conventional floor for print body text.
 */
export const LEGIBILITY_FLOOR_PT = 8;

/**
 * The advisory shown when the base font size, once shrunk by the tiling's
 * uniform scale, lands below the legibility floor — or null when it does not.
 *
 * Advisory by decision: the chosen fit is always honoured — silently refusing
 * it would be worse than small type the user knowingly asked for — but the
 * tradeoff is stated rather than left for the printer to reveal.
 *
 * Names the *effective* size rather than the raw percentage: a percentage
 * means nothing without knowing the base size it applies to, and the base is
 * now the user's own choice (spec R1).
 */
export function legibilityWarning(scalePercent: number, fontSizePt: number): string | null {
  const effectivePt = (fontSizePt * scalePercent) / 100;
  if (effectivePt >= LEGIBILITY_FLOOR_PT) return null;
  const displayed = Math.round(effectivePt * 2) / 2;
  return `Police effective ${displayed} pt : peu lisible à l'impression. Décochez des colonnes ou choisissez « ${FIT_LABELS.aucun} ».`;
}

export function toggleSubjectDescriptionInclude(
  options: ExportSubjectDescriptionOptions,
): ExportSubjectDescriptionOptions {
  return { ...options, include: !options.include };
}

export function setSubjectDescriptionPosition(
  options: ExportSubjectDescriptionOptions,
  position: 'before' | 'after',
): ExportSubjectDescriptionOptions {
  return { ...options, position };
}
